import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { SIDE_ICON_COLOR } from "../data/mockData";
import type { AndamentoProject } from "../types/timeEfficiency.types";
import { fmtDias } from "../utils/format";
import {
  TE_CARD_SX,
  TE_COLORS,
  TE_PANEL_DESC_SX,
  TE_PANEL_TITLE_SX,
} from "./theme";

interface StaleProjectsListProps {
  items: AndamentoProject[];
  total: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  loading?: boolean;
}

export function StaleProjectsList({
  items,
  total,
  expanded,
  onToggleExpanded,
  loading,
}: StaleProjectsListProps) {
  const restante = total - items.length;

  return (
    <Box sx={TE_CARD_SX}>
      <Typography sx={{ ...TE_PANEL_TITLE_SX, mb: 0.5 }}>
        Projetos parados por mais tempo
      </Typography>
      <Typography sx={{ ...TE_PANEL_DESC_SX, mb: 1.5 }}>
        Projetos em andamento com maior tempo no status atual.
      </Typography>

      {loading && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ py: 5 }}
        >
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && !items.length && (
        <Typography
          sx={{
            color: TE_COLORS.textFaint,
            fontSize: 12.5,
            py: 2.5,
            textAlign: "center",
          }}
        >
          Nenhum projeto em andamento no filtro selecionado.
        </Typography>
      )}

      {!loading && (
        <Box
          display="flex"
          flexDirection="column"
          sx={{ maxHeight: 460, overflowY: "auto" }}
        >
          {items.map((item, i) => {
            const color = SIDE_ICON_COLOR[i % SIDE_ICON_COLOR.length];
            return (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 1,
                  borderBottom: "1px solid #F1F2F7",
                  "&:last-of-type": { borderBottom: "none" },
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    bgcolor: `${color}22`,
                    color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  ●
                </Box>
                <Box flex={1} minWidth={0}>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: TE_COLORS.text,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        color: TE_COLORS.textFaint,
                        fontWeight: 500,
                        fontSize: 11,
                        mr: 0.5,
                      }}
                    >
                      {item.id}
                    </Typography>
                    {item.projeto}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: TE_COLORS.textDim }}>
                    {item.status}
                  </Typography>
                  {item.justificativa && item.justificativa !== "–" && (
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: TE_COLORS.textFaint,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.justificativa}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={
                    item.tempoNoStatusDisponivel === false
                      ? {
                          fontSize: 11,
                          fontStyle: "italic",
                          color: TE_COLORS.textFaint,
                          flexShrink: 0,
                        }
                      : {
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: TE_COLORS.red,
                          flexShrink: 0,
                        }
                  }
                  title={
                    item.tempoNoStatusDisponivel === false
                      ? "Projeto sem histórico de status registrado -- não é possível calcular o tempo real na etapa atual."
                      : undefined
                  }
                >
                  {item.tempoNoStatusDisponivel === false
                    ? "Sem histórico"
                    : fmtDias(item.tempoNoStatus)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {!loading && (expanded || restante > 0) && (
        <Link
          component="button"
          onClick={onToggleExpanded}
          sx={{
            display: "block",
            mt: 1.5,
            fontSize: 12.5,
            fontWeight: 600,
            color: TE_COLORS.blue,
          }}
        >
          {expanded
            ? "Mostrar menos ←"
            : `Ver todos os projetos parados (+${restante}) →`}
        </Link>
      )}
    </Box>
  );
}
